import { OverlayLayerContext, useOverlayLayer } from "./OverlayLayer";
import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../lib/cn";
import { menuItemClassName } from "./DropdownMenu";
import { popupClassName } from "./Popover";

type WithClassName<P> = Omit<P, "className"> & { className?: string };

const Root = BaseSelect.Root;
const Group = BaseSelect.Group;

export interface SelectTriggerProps extends WithClassName<BaseSelect.Trigger.Props> {
  placeholder?: string;
  size?: "sm" | "md";
}

function Trigger({ className, children, placeholder, size = "md", ...props }: SelectTriggerProps) {
  return (
    <BaseSelect.Trigger
      data-slot="select-trigger"
      className={cn(
        "flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-bg pr-1.5 pl-2.5 text-sm text-fg whitespace-nowrap field-focus select-none hover:border-border-strong data-disabled:cursor-not-allowed data-disabled:opacity-50 data-popup-open:border-border-strong",
        size === "sm" ? "h-7 text-xs" : "h-8",
        className,
      )}
      {...props}
    >
      {children ?? (
        <BaseSelect.Value
          data-slot="select-value"
          className="truncate data-placeholder:text-fg-faint"
          {...(placeholder !== undefined ? { placeholder } : {})}
        />
      )}
      <BaseSelect.Icon data-slot="select-icon" className="flex shrink-0 text-fg-muted">
        <ChevronsUpDown className="size-3.5" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
}

export interface SelectContentProps extends WithClassName<BaseSelect.Popup.Props> {
  side?: BaseSelect.Positioner.Props["side"];
  align?: BaseSelect.Positioner.Props["align"];
  sideOffset?: number;
  /** Align the popup over the trigger like a native select (`false`) or drop below it (`true`, default). */
  alignItemWithTrigger?: boolean;
}

function Content({
  className,
  children,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  alignItemWithTrigger = false,
  ...props
}: SelectContentProps) {
  const layer = useOverlayLayer("dropdown");
  return (
    <OverlayLayerContext value={layer}>
      <BaseSelect.Portal>
        <BaseSelect.Positioner
          style={{ zIndex: layer }}
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignItemWithTrigger={alignItemWithTrigger}
          className="z-dropdown outline-none select-none"
        >
          <BaseSelect.Popup
            data-slot="select-content"
            className={cn(
              popupClassName,
              "min-w-(--anchor-width) p-1 data-[side=none]:data-starting-style:scale-100 data-[side=none]:data-starting-style:opacity-100",
              className,
            )}
            {...props}
          >
            <BaseSelect.List className="max-h-(--available-height) overflow-y-auto scroll-py-1">
              {children}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </OverlayLayerContext>
  );
}

function Item({ className, children, ...props }: WithClassName<BaseSelect.Item.Props>) {
  return (
    <BaseSelect.Item
      data-slot="select-item"
      className={cn(menuItemClassName, "pr-8", className)}
      {...props}
    >
      <BaseSelect.ItemText className="truncate">{children}</BaseSelect.ItemText>
      <BaseSelect.ItemIndicator className="absolute right-2 flex items-center justify-center">
        <Check className="size-3.5 text-fg" strokeWidth={2.5} />
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  );
}

function GroupLabel({ className, ...props }: WithClassName<BaseSelect.GroupLabel.Props>) {
  return (
    <BaseSelect.GroupLabel
      data-slot="select-group-label"
      className={cn("px-2 py-1.5 text-xs font-medium text-fg-muted", className)}
      {...props}
    />
  );
}

function Separator({ className, ...props }: WithClassName<BaseSelect.Separator.Props>) {
  return (
    <BaseSelect.Separator
      data-slot="select-separator"
      className={cn("-mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

/**
 * Anatomy: `Select.Root value onValueChange items? > Select.Trigger placeholder + Select.Content > (Select.Item value)*`.
 * Pass `items` (a `{ value, label }[]` or record) on Root so the trigger can show the label of the selected value.
 */
export const Select = { Root, Trigger, Content, Item, Group, GroupLabel, Separator };
